import * as React from "react";

import type { FileRecord } from "@/shared/domain/files";
import { useSetJointBindings } from "@/shared/domain/urdf/hooks";
import type {
  Joint,
  JointBinding,
} from "@/shared/state/visualization/schema/v1/three-d";

import { declaredJointBindings } from "./jointBindingDraft";

export interface JointBindingDraft {
  /**
   * The complete set a save would write: what the model declares, with the
   * user's edits laid over it.
   */
  bindings: Record<string, JointBinding>;
  /** Edits one joint's declaration, or removes it with `undefined`. */
  onBindingChange: (
    jointName: string,
    binding: JointBinding | undefined,
  ) => void;
  /** Takes the declarations a freshly-parsed model carries as the base. */
  onJointsLoaded: (joints: readonly Joint[]) => void;
  /**
   * Sends the whole set, resolving `true` once the platform has accepted it.
   *
   * `false` covers both refusals and there being nothing to address a save to;
   * the reason, when there is one, is in {@link error}.
   */
  save: () => Promise<boolean>;
  /** Whether a save is in flight, for the button that started it. */
  isSaving: boolean;
  /** Why the last save was refused, if it was. */
  error: string | undefined;
  /** Drops every edit and every declaration read off a model. */
  clear: () => void;
}

/**
 * The joint declarations a wizard is about to write, and the act of writing
 * them.
 *
 * ## Why this is a diff and not one seeded map
 *
 * The two halves are held apart — what the *model* declares, and what the *user*
 * changed — because the model's declarations arrive **late**: the glTF is
 * fetched and parsed well after the dialog opens, so there is no seed to take at
 * mount time and a state initialiser would capture an empty one forever. Kept as
 * a diff, the two compose in render order and an edit made before the parse
 * lands still survives it.
 *
 * `null` rather than deletion is the third state clearing needs. A cleared row
 * that read as merely "untouched" would have the model's own declaration
 * reappear underneath it, so a save would faithfully restore what the user had
 * just deleted.
 *
 * ## Why the merge is composed rather than accumulated
 *
 * A save replaces the **complete** set. A draft that held only the edits would
 * clear every joint the user never looked at — one row edited, a robot's worth
 * of declarations gone, with no error and no warning.
 *
 * ## What it is not
 *
 * It is not durable, and nothing here pretends otherwise: everything it holds is
 * dropped by {@link clear}, which the dialog runs once it has finished closing.
 * Abandoning the wizard therefore abandons whatever was never sent — and only
 * that. A save the platform has already accepted is a worker's job by then, and
 * no state in this hook can call it back.
 *
 * @param sourceFile the **source** `.urdf` a save is addressed by; the route
 *   derives the artifact it patches. `undefined` leaves the draft unsaveable,
 *   which is what a dialog that has already begun closing has.
 */
export function useJointBindingDraft(
  sourceFile: Pick<FileRecord, "file_id" | "org_id"> | undefined,
): JointBindingDraft {
  const [edits, setEdits] = React.useState<Record<string, JointBinding | null>>(
    {},
  );
  const [declaredByModel, setDeclaredByModel] = React.useState<
    Record<string, JointBinding>
  >({});

  const mutation = useSetJointBindings();
  const { mutateAsync, reset } = mutation;

  const bindings = React.useMemo(() => {
    const merged: Record<string, JointBinding> = { ...declaredByModel };
    for (const [jointName, binding] of Object.entries(edits)) {
      if (binding === null) {
        delete merged[jointName];
      } else {
        merged[jointName] = binding;
      }
    }
    return merged;
  }, [declaredByModel, edits]);

  const onBindingChange = React.useCallback(
    (jointName: string, binding: JointBinding | undefined) => {
      setEdits((current) => ({ ...current, [jointName]: binding ?? null }));
    },
    [],
  );

  const onJointsLoaded = React.useCallback((joints: readonly Joint[]) => {
    setDeclaredByModel(declaredJointBindings(joints));
  }, []);

  /**
   * Awaited rather than fired, because what happens next depends on the answer:
   * the dialog closes on acceptance and stays up on refusal, and a refusal
   * reported into a dialog that had already gone would be reported nowhere. The
   * rejection is swallowed here and turned into `false` — it is shown from
   * {@link error} — since an exception escaping a click handler goes nowhere a
   * user can see.
   */
  const save = React.useCallback(async () => {
    if (sourceFile === undefined) {
      return false;
    }
    try {
      await mutateAsync({ file: sourceFile, bindings });
      return true;
    } catch {
      return false;
    }
  }, [bindings, mutateAsync, sourceFile]);

  const clear = React.useCallback(() => {
    setEdits({});
    setDeclaredByModel({});
    reset();
  }, [reset]);

  return {
    bindings,
    onBindingChange,
    onJointsLoaded,
    save,
    isSaving: mutation.isPending,
    error: mutation.error?.message,
    clear,
  };
}
